import { logError } from '../Shared/Logger';
import { hasFocusWithin } from '../Shared/DomUtils';
import { initSmartMenuAccessibility } from './SmartMenuAccessibility/Init';
import { clearShowcaseDismissClass } from './SmartMenuAccessibility/Utils';

// =============================================================================
// Type Definitions
// =============================================================================

interface SmartMenusConfig {
    subMenusSubOffsetX?: number;
    subMenusSubOffsetY?: number;
    mainMenuSubOffsetY?: number;
    subIndicators?: boolean;
    collapsibleShowFunction?: (() => void) | null;
    collapsibleHideFunction?: (() => void) | null;
    keepInViewport?: boolean;
    subMenusMinWidth?: string | null;
    subMenusMaxWidth?: string | null;
}

interface SmartMenusPrototype {
    itemActivate: ($a: JQuery, focus?: boolean) => void;
    menuPosition: ($sub: JQuery) => void;
    menuHideSubMenus: (level: number) => void;
    activatedItems: JQuery[];
    menuHide: (sub: JQuery) => void;
}

declare global {
    interface Window {
        jQuery: JQueryStatic;
    }

    interface JQuery {
        smartmenus(config?: SmartMenusConfig): JQuery;
        dataSM(key: string, value?: unknown): unknown;
    }

    interface JQueryStatic {
        SmartMenus: {
            prototype: SmartMenusPrototype;
        };
    }
}

// =============================================================================
// Configuration
// =============================================================================

const SMART_MENU_CONFIG: SmartMenusConfig = {
    subMenusSubOffsetX: 2,
    subMenusSubOffsetY: -19,
    mainMenuSubOffsetY: -1,
    subIndicators: false,
    collapsibleShowFunction: null,
    collapsibleHideFunction: null,
    keepInViewport: true,
    subMenusMinWidth: null,
    subMenusMaxWidth: null,
};

/** Width of showcase menu panel in pixels */
const SHOWCASE_MENU_WIDTH = 656;

/** Margin from viewport edge in pixels */
const VIEWPORT_MARGIN = 10;

/** Selectors for smart menu initialization */
const MENU_SELECTORS = [
    '.ss-site-header-page-links',
    '.ss-site-header-main-nav .ss-site-header-main-links-container',
    '.ss-site-header-main-nav-top .js-site-header-main-links-container-right',
    '.ss-site-header-main-container .ss-site-header-main-links-container',
] as const;

// =============================================================================
// jQuery Reference (deferred to runtime)
// =============================================================================

// We need to defer getting jQuery until runtime to get the CDN version
// that has SmartMenus attached, not the Vite-injected version
const getJQuery = (): JQueryStatic | undefined => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = globalThis as any;
    return win.jQuery || win.$;
};

let $: JQueryStatic;

// =============================================================================
// Showcase Menu Positioning
// =============================================================================

/**
 * Position a showcase menu within the viewport.
 *
 * This replaces SmartMenus' menuPosition for showcase menus because SmartMenus
 * calculates positioning based on the menu's actual width, but showcase menus
 * are 656px wide - much larger than standard dropdowns.
 */
const positionShowcaseMenu = ($sub: JQuery): void => {
    if (!$sub.hasClass('ss-dropdown-menu-with-showcase')) {
        return;
    }

    // Get parent anchor from SmartMenus data, or fall back to DOM traversal
    let $a = $sub.dataSM('parent-a') as JQuery | undefined;
    if (!$a || !$a.length) {
        $a = $sub.parent('li').children('a').first();
    }

    if (!$a || !$a.length) {
        return;
    }

    try {
        const $win = $(window);
        const winScrollLeft = $win.scrollLeft() ?? 0;
        const winWidth = $win.width() ?? 0;
        const viewportRight = winScrollLeft + winWidth;

        const itemOffset = $a.offset();
        if (!itemOffset) {
            return;
        }

        const itemX = itemOffset.left;
        const menuRight = itemX + SHOWCASE_MENU_WIDTH;

        // Calculate horizontal offset to keep menu in viewport
        let marginLeft = 0;
        if (menuRight > viewportRight - VIEWPORT_MARGIN) {
            const overflow = menuRight - (viewportRight - VIEWPORT_MARGIN);
            const minMarginLeft = winScrollLeft + VIEWPORT_MARGIN - itemX;
            marginLeft = Math.max(-overflow, minMarginLeft);
        }

        $sub.css({
            top: 'auto',
            left: '0',
            marginLeft: marginLeft,
            marginTop: SMART_MENU_CONFIG.mainMenuSubOffsetY ?? -1,
        });
    } catch (error) {
        logError('SmartMenu', 'Positioning failed:', { element: $sub, error });
    }
};

// =============================================================================
// Showcase Menu State Management
// =============================================================================

/**
 * Activate a showcase menu (position, highlight, update aria attributes).
 *
 * Updates aria-hidden on:
 * - The showcase menu itself ($sub)
 * - The submenu links container (.ss-submenu-links > ul)
 *
 * Deeper nested submenus retain aria-hidden="true" until their parent nav link
 * is hovered/focused, at which point SmartMenus handles them via menuShow.
 */
const activateShowcaseMenu = ($a: JQuery, $sub: JQuery): void => {
    if ($a.hasClass('highlighted')) {
        return;
    }

    // Remove any Esc-dismissal class so a fresh hover/focus interaction
    // can show the menu again (see SHOWCASE_DISMISSED_CLASS in Utils.ts).
    clearShowcaseDismissClass($sub);

    positionShowcaseMenu($sub);
    $a.addClass('highlighted');
    // Showcase wrapper is role="presentation"; only aria-hidden is permitted as
    // a non-default global ARIA property without invalidating the role.
    // The disclosure button below carries aria-expanded for state.
    $sub.attr('aria-hidden', 'false');

    // Update disclosure button state (aria-expanded lives on the button)
    const $button = $a.siblings('.ss-menu-disclosure-button').first();
    if ($button.length) {
        $button.attr('aria-expanded', 'true');
    }

    // Also reveal the submenu links container (first-level ul inside .ss-submenu-links).
    // aria-expanded is not set here — it belongs on the disclosure button only.
    $sub.find('.ss-submenu-links > ul').attr('aria-hidden', 'false');
};

/**
 * Reset showcase menu state (remove highlight, reset aria attributes).
 * Only resets if the menu is active and focus is not inside.
 */
const resetShowcaseMenuState = ($a: JQuery, $sub: JQuery): void => {
    if (!$a.hasClass('highlighted')) {
        return;
    }

    if (hasFocusWithin($sub)) {
        return;
    }

    $a.removeClass('highlighted');
    // Remove any Esc-dismissal class so normal CSS controls visibility again.
    clearShowcaseDismissClass($sub);
    // Showcase wrapper stays role="presentation"; aria-expanded is intentionally
    // not set here (see activateShowcaseMenu). aria-hidden is permitted.
    $sub.attr('aria-hidden', 'true');

    // Update disclosure button state (aria-expanded lives on the button)
    const $button = $a.siblings('.ss-menu-disclosure-button').first();
    if ($button.length) {
        $button.attr('aria-expanded', 'false');
    }

    // Reset all nested submenus (including .ss-submenu-links > ul) to hidden state.
    // aria-expanded is not set on these elements — it belongs on the disclosure button only.
    $sub.find('ul[aria-hidden]').attr('aria-hidden', 'true');
};

// =============================================================================
// SmartMenus Prototype Overrides
// =============================================================================

/**
 * Override SmartMenus prototype methods to handle showcase menus.
 *
 * Showcase menus use a two-column wrapper UL structure:
 *   <ul class="ss-dropdown-menu-with-showcase">
 *     <li class="ss-showcase-menu-panel">...</li>
 *     <li class="ss-submenu-links"><ul>...</ul></li>
 *   </ul>
 *
 * This structure can cause SmartMenus to pass undefined/empty jQuery objects
 * to internal methods. These overrides add necessary guards.
 */
const applyPrototypeOverrides = (): void => {
    // Override menuPosition for showcase menus (custom width handling)
    const originalMenuPosition = $.SmartMenus.prototype.menuPosition;
    $.SmartMenus.prototype.menuPosition = function ($sub: JQuery): void {
        if ($sub.hasClass('ss-dropdown-menu-with-showcase')) {
            positionShowcaseMenu($sub);
            return;
        }
        return originalMenuPosition.apply(this, [$sub]);
    };

    // Guard against undefined/empty $a in itemActivate
    const originalItemActivate = $.SmartMenus.prototype.itemActivate;
    $.SmartMenus.prototype.itemActivate = function ($a: JQuery, focus?: boolean): void {
        if (!$a || !$a.length) {
            return;
        }
        return originalItemActivate.apply(this, [$a, focus]);
    };

    // Guard against undefined activatedItems in menuHideSubMenus
    $.SmartMenus.prototype.menuHideSubMenus = function (this: SmartMenusPrototype, level: number): void {
        for (let i = this.activatedItems.length - 1; i >= level; i--) {
            if (!this.activatedItems[i]) {
                continue;
            }
            const $sub = this.activatedItems[i].dataSM('sub') as JQuery | undefined;
            if ($sub) {
                this.menuHide($sub);
            }
        }
    };
};

// =============================================================================
// Event Bindings
// =============================================================================

/**
 * Bind hover and focus events to showcase menu parent items.
 *
 * CSS `:hover` rules show showcase menus before SmartMenus' JavaScript runs.
 * SmartMenus checks `if (!$sub.is(':visible'))` in menuShow, so it skips
 * positioning and state changes for CSS-shown menus. We handle these directly.
 */
const bindShowcaseMenuEvents = (): void => {
    $('.ss-site-header-page-links .top-nav').each(function () {
        const $li = $(this);
        const $sub = $li.children('ul.ss-dropdown-menu-with-showcase').first();

        if (!$sub.length) {
            return;
        }

        const $a = $li.children('a').first();

        // Activate on mouse hover
        $li.on('mouseenter', () => activateShowcaseMenu($a, $sub));

        // Activate on keyboard focus entering the submenu (not parent anchor)
        // This matches regular menus where Enter/Space is required to open
        $sub.on('focusin', () => activateShowcaseMenu($a, $sub));

        // Reset on mouse leave (unless keyboard focus is inside)
        $li.on('mouseleave', () => {
            if (!hasFocusWithin($li)) {
                resetShowcaseMenuState($a, $sub);
            }
        });

        // Reset on focus leaving the menu item
        $li.on('focusout', () => {
            // Defer to allow focus to settle on new target
            setTimeout(() => {
                if (!hasFocusWithin($li) && !$li.is(':hover')) {
                    resetShowcaseMenuState($a, $sub);
                }
            }, 0);
        });
    });
};

/**
 * Prevent SmartMenus from hiding menus when focus is inside.
 * This avoids browser warnings about aria-hidden on focused elements.
 */
const bindSmartMenusHideProtection = (): void => {
    $('.ss-site-header-page-links').on('beforehide.smapi', (_e, menu) => {
        const $sub = $(menu);
        const isShowcaseOrNested =
            $sub.hasClass('ss-dropdown-menu-with-showcase') ||
            $sub.closest('.ss-dropdown-menu-with-showcase').length > 0;

        if (isShowcaseOrNested && hasFocusWithin($sub)) {
            return false;
        }
    });
};

// =============================================================================
// Initialization
// =============================================================================

/**
 * Initialize SmartMenu functionality.
 */
const initSmartMenu = (): void => {
    const jq = getJQuery();
    if (!jq) {
        logError('SmartMenu', 'jQuery not available');
        return;
    }
    $ = jq;

    if (!$.fn?.smartmenus || !$.SmartMenus) {
        logError('SmartMenu', 'SmartMenus plugin not available');
        return;
    }

    try {
        applyPrototypeOverrides();

        // Initialize SmartMenus on all navigation elements
        MENU_SELECTORS.forEach((selector) => $(selector).smartmenus(SMART_MENU_CONFIG));

        // Add accessibility enhancements (disclosure buttons, ARIA migration)
        // Must run AFTER SmartMenus init so we can read the ARIA attributes it adds
        initSmartMenuAccessibility(MENU_SELECTORS);

        bindShowcaseMenuEvents();
        bindSmartMenusHideProtection();

        // Reposition showcase menus on window resize (debounced)
        let resizeTimeout: number | undefined;
        $(window).on('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(() => {
                $('.ss-site-header-page-links .ss-dropdown-menu-with-showcase')
                    .filter(':visible')
                    .each(function () {
                        positionShowcaseMenu($(this));
                    });
            }, 150);
        });
    } catch (error) {
        logError('SmartMenu', 'Initialization failed:', error);
    }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmartMenu);
} else {
    initSmartMenu();
}
